import EmptyState from "../components/EmptyState";
import ErrorAlert from "../components/ErrorAlert";
import LoadingState from "../components/LoadingState";
import PageTitle from "../components/PageTitle";
import StatusBadge from "../components/StatusBadge";
import useDashboard from "../hooks/useDashboard";
import { formatCurrency, formatRequestedAt } from "../utils/formatters";
import DashboardAlerts from "./DashboardAlerts";
import DashboardSummary from "./DashboardSummary";
import PopularItems from "./PopularItems";
import RecentOrders from "./RecentOrders";

function Panel({ title, children, wide = false }) {
  return (
    <section className={`card admin-panel ${wide ? "is-wide" : ""}`}>
      <header className="card-header">
        <h2>{title}</h2>
      </header>
      <div className="card-body">{children}</div>
    </section>
  );
}

function AttentionOrders({ orders }) {
  if (!orders?.length) {
    return <EmptyState />;
  }

  return (
    <div className="admin-list">
      {orders.map((order) => (
        <article key={order.id}>
          <strong>{order.order_number}</strong>
          <span>
            {order.customer_name} - {order.location_name}
          </span>
          <span>{formatRequestedAt(order.requested_date, order.requested_time)}</span>
          <StatusBadge status={order.status} />
        </article>
      ))}
    </div>
  );
}

function LowPerformingItems({ items }) {
  if (!items?.length) {
    return <EmptyState />;
  }

  return (
    <div className="admin-list">
      {items.map((item) => (
        <article key={item.id}>
          <strong>{item.name}</strong>
          <span>{item.category_name}</span>
          <span>{formatCurrency(item.base_price)}</span>
        </article>
      ))}
    </div>
  );
}

function SalesByLocation({ locations }) {
  if (!locations?.length) {
    return <EmptyState />;
  }

  return (
    <div className="admin-list">
      {locations.map((location) => (
        <article key={location.location}>
          <strong>{location.location__name}</strong>
          <span>{location.order_count} orders</span>
          <span>{formatCurrency(location.revenue)}</span>
        </article>
      ))}
    </div>
  );
}

function WeeklyMenuOverview({ menus }) {
  return (
    <div className="mini-grid">
      {menus.map((menu) => (
        <article className="location-card mini-card" key={menu.location_id}>
          <strong>{menu.location}</strong>
          <span>{menu.menu_name}</span>
          <span>{menu.date_range || "No date range"}</span>
          <span>
            {menu.active_items} active - {menu.sold_out_items} sold out
          </span>
          <span className={menu.is_published ? "badge badge-success" : "badge badge-warning"}>
            {menu.status}
          </span>
        </article>
      ))}
    </div>
  );
}

function CateringActivity({ requests }) {
  if (!requests?.length) {
    return <EmptyState />;
  }

  return (
    <div className="admin-list">
      {requests.map((request) => (
        <article key={request.id}>
          <strong>{request.customer_name}</strong>
          <span>
            {request.event_date} - {request.guest_count} guests
          </span>
          <span>
            {request.preferred_location_name} - {request.status}
          </span>
        </article>
      ))}
    </div>
  );
}

export default function DashboardPage({ filters, onNavigate }) {
  const { dashboard, error, isLoading, reloadDashboard } = useDashboard(filters);

  if (error) {
    return (
      <main className="admin-page">
        <ErrorAlert error={error} onRetry={reloadDashboard} />
      </main>
    );
  }

  if (isLoading || !dashboard) {
    return (
      <main className="admin-page">
        <LoadingState />
      </main>
    );
  }

  return (
    <main className="admin-page flex flex-column gap-5">
      <PageTitle eyebrow="Owner dashboard" title="Today at a glance">
        Live order, catering, menu, and sales signals.
      </PageTitle>

      <DashboardSummary summary={dashboard.summary} />

      <section className="admin-grid">
        <Panel title="Orders Requiring Attention">
          <AttentionOrders orders={dashboard.orders_requiring_attention} />
        </Panel>

        <Panel title="Alerts">
          <DashboardAlerts alerts={dashboard.alerts} />
        </Panel>

        <Panel title="Recent Orders" wide>
          <RecentOrders
            orders={dashboard.recent_orders}
            onOpenOrder={(orderId) => onNavigate(`/admin/orders/${orderId}`)}
          />
        </Panel>

        <Panel title="Popular Items">
          <PopularItems items={dashboard.popular_items} />
        </Panel>

        <Panel title="Low-Performing Items">
          <LowPerformingItems items={dashboard.low_performing_items} />
        </Panel>

        <Panel title="Sales by Location">
          <SalesByLocation locations={dashboard.sales_by_location} />
        </Panel>

        <Panel title="Weekly Menu Overview" wide>
          <WeeklyMenuOverview menus={dashboard.weekly_menu_overview} />
        </Panel>

        <Panel title="Catering Activity">
          <CateringActivity requests={dashboard.catering_activity} />
        </Panel>
      </section>
    </main>
  );
}